import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PasswordRequirements, { isPasswordValid, passwordError } from '../components/PasswordRequirements';
import { clearWebOfflineCaches } from '../utils/offlineStorage';
import { usersAPI, tenantsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { User, UserBrief, UserRole, UserCreateRequest, UserUpdateRequest } from '../types';
import FilialeSelector from '../components/FilialeSelector';
import ConfirmModal from '../components/ConfirmModal';
import UserHierarchy from '../components/UserHierarchy';

// Role labels for display
const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Amministratore',
  manager: 'Manager',
  commerciale: 'Commerciale',
  viewer: 'Visualizzatore',
  cliente: 'Cliente',
};

// Role colors for badges
const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  commerciale: 'bg-green-100 text-green-800',
  viewer: 'bg-gray-100 text-gray-800',
  cliente: 'bg-amber-100 text-amber-800',
};

interface UserFormData {
  email: string;
  nome: string;
  cognome: string;
  password: string;
  role: UserRole;
  is_active: boolean;
  custom_permissions: string[];
  denied_permissions: string[];
  telefono: string;
  telegram_pin: string;
  filiale_id: string;
  responsabile_id: string;
  codice_esterno: string;
}

// Cono di visibilità (issue #54): discendenti (diretti+indiretti) di un utente nella
// gerarchia flat, per escluderli dalle opzioni del picker "Responsabile" (nicety UX —
// la validazione anti-ciclo reale resta lato backend).
function getDescendantIds(users: UserBrief[], rootId: string): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const u of users) {
    if (u.responsabile_id) {
      const list = childrenByParent.get(u.responsabile_id) || [];
      list.push(u.id);
      childrenByParent.set(u.responsabile_id, list);
    }
  }
  const result = new Set<string>();
  const stack = [...(childrenByParent.get(rootId) || [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (result.has(id)) continue;
    result.add(id);
    stack.push(...(childrenByParent.get(id) || []));
  }
  return result;
}

// Profondità di ciascun utente nella gerarchia (root = 0), per indentare il picker.
function getDepthMap(users: UserBrief[]): Map<string, number> {
  const byId = new Map(users.map((u) => [u.id, u]));
  const cache = new Map<string, number>();
  function depthOf(id: string, seen: Set<string>): number {
    if (cache.has(id)) return cache.get(id)!;
    if (seen.has(id)) return 0; // ciclo pre-esistente, difensivo
    const u = byId.get(id);
    if (!u || !u.responsabile_id) {
      cache.set(id, 0);
      return 0;
    }
    const d = 1 + depthOf(u.responsabile_id, new Set(seen).add(id));
    cache.set(id, d);
    return d;
  }
  const map = new Map<string, number>();
  for (const u of users) map.set(u.id, depthOf(u.id, new Set()));
  return map;
}

interface UserFormProps {
  user?: User | null;
  onSubmit: (data: UserCreateRequest | UserUpdateRequest) => void;
  onClose: () => void;
  isLoading: boolean;
  isHostMode?: boolean;
}

const UserForm = ({ user, onSubmit, onClose, isLoading, isHostMode }: UserFormProps) => {
  const [formData, setFormData] = useState<UserFormData>({
    email: user?.email || '',
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    password: '',
    role: user?.role || 'commerciale',
    is_active: user?.is_active ?? true,
    custom_permissions: user?.custom_permissions || [],
    denied_permissions: user?.denied_permissions || [],
    telefono: user?.telefono || '',
    telegram_pin: '',
    filiale_id: user?.filiale_id || '',
    responsabile_id: user?.responsabile_id || '',
    codice_esterno: user?.codice_esterno || '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvancedPermissions, setShowAdvancedPermissions] = useState(false);

  // Fetch available permissions
  const { data: permissionsData } = useQuery({
    queryKey: ['roles-permissions'],
    queryFn: async () => {
      const response = await usersAPI.getRolesPermissions();
      return response.data;
    },
  });

  // Cono di visibilità (issue #54): utenti del tenant per il picker "Responsabile"
  const { data: hierarchyUsers } = useQuery({
    queryKey: ['users-hierarchy'],
    queryFn: async () => {
      const response = await usersAPI.getHierarchy();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const excludedIds = user
    ? new Set([user.id, ...getDescendantIds(hierarchyUsers || [], user.id)])
    : new Set<string>();
  const depthMap = getDepthMap(hierarchyUsers || []);
  const responsabileOptions = (hierarchyUsers || [])
    .filter((u) => !excludedIds.has(u.id))
    .sort((a, b) => (depthMap.get(a.id) || 0) - (depthMap.get(b.id) || 0) || a.name.localeCompare(b.name));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (user) {
      // Update: only send changed fields
      const updateData: UserUpdateRequest = {};
      if (formData.nome !== user.nome) updateData.nome = formData.nome;
      if (formData.cognome !== user.cognome) updateData.cognome = formData.cognome;
      if (formData.role !== user.role) updateData.role = formData.role;
      if (formData.is_active !== user.is_active) updateData.is_active = formData.is_active;
      if (JSON.stringify(formData.custom_permissions) !== JSON.stringify(user.custom_permissions)) {
        updateData.custom_permissions = formData.custom_permissions;
      }
      if (JSON.stringify(formData.denied_permissions) !== JSON.stringify(user.denied_permissions)) {
        updateData.denied_permissions = formData.denied_permissions;
      }
      if (formData.telefono !== (user.telefono || '')) updateData.telefono = formData.telefono || undefined;
      if (formData.telegram_pin) updateData.telegram_pin = formData.telegram_pin;
      if (formData.filiale_id !== (user.filiale_id || '')) updateData.filiale_id = formData.filiale_id || null;
      if (formData.responsabile_id !== (user.responsabile_id || '')) {
        updateData.responsabile_id = formData.responsabile_id || null;
      }
      if (formData.codice_esterno !== (user.codice_esterno || '')) {
        updateData.codice_esterno = formData.codice_esterno || null;
      }
      onSubmit(updateData);
    } else {
      if (!isPasswordValid(formData.password)) return;
      // Create
      const createData: UserCreateRequest = {
        email: formData.email,
        nome: formData.nome,
        cognome: formData.cognome,
        password: formData.password,
        role: formData.role,
        is_active: formData.is_active,
        custom_permissions: formData.custom_permissions,
        denied_permissions: formData.denied_permissions,
      };
      if (formData.telefono) createData.telefono = formData.telefono;
      if (formData.filiale_id) createData.filiale_id = formData.filiale_id;
      if (formData.responsabile_id) createData.responsabile_id = formData.responsabile_id;
      if (formData.codice_esterno) createData.codice_esterno = formData.codice_esterno;
      onSubmit(createData);
    }
  };

  const togglePermission = (permission: string, type: 'custom' | 'denied') => {
    if (type === 'custom') {
      setFormData(prev => ({
        ...prev,
        custom_permissions: prev.custom_permissions.includes(permission)
          ? prev.custom_permissions.filter(p => p !== permission)
          : [...prev.custom_permissions, permission],
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        denied_permissions: prev.denied_permissions.includes(permission)
          ? prev.denied_permissions.filter(p => p !== permission)
          : [...prev.denied_permissions, permission],
      }));
    }
  };

  // Get permissions for the current role
  const rolePermissions = permissionsData?.roles?.[formData.role] || [];
  // Convert permissions array to Record for easy lookup
  const allPermissions: Record<string, { label?: string; description?: string }> =
    (permissionsData?.all_permissions || []).reduce((acc, perm) => {
      acc[perm.key] = { label: perm.name, description: perm.description };
      return acc;
    }, {} as Record<string, { label?: string; description?: string }>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {user ? 'Modifica Utente' : isHostMode ? 'Nuovo Utente Host' : 'Nuovo Utente'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {!user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="email@esempio.it"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                type="text"
                required
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
              <input
                type="text"
                required
                value={formData.cognome}
                onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Telegram Section */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+39 333 1234567"
              />
              <p className="text-xs text-gray-500 mt-1">
                Per autenticazione Telegram Bot
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PIN Telegram
                {user?.telegram_connected && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                    Connesso
                  </span>
                )}
              </label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]{4,6}"
                maxLength={6}
                value={formData.telegram_pin}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setFormData({ ...formData, telegram_pin: val });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={user ? 'Lascia vuoto per non cambiare' : '4-6 cifre'}
              />
              <p className="text-xs text-gray-500 mt-1">
                PIN numerico 4-6 cifre per accesso bot
              </p>
            </div>
          </div>

          {!user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  placeholder="Minimo 8 caratteri"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <PasswordRequirements password={formData.password} />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {!isHostMode && <FilialeSelector value={formData.filiale_id} onChange={(value) => setFormData({ ...formData, filiale_id: value })} allowCreate />}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsabile</label>
              <select
                value={formData.responsabile_id}
                onChange={(e) => setFormData({ ...formData, responsabile_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Nessuno</option>
                {responsabileOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {'— '.repeat(depthMap.get(u.id) || 0)}{u.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Determina il cono di visibilità: chi vede i dati di chi
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice esterno</label>
              <input
                type="text"
                value={formData.codice_esterno}
                onChange={(e) => setFormData({ ...formData, codice_esterno: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="es. AG001"
              />
              <p className="text-xs text-gray-500 mt-1">
                Usato per import futuri da sistemi esterni (AS400)
              </p>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Utente attivo
            </label>
          </div>

          {/* Advanced Permissions Section */}
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowAdvancedPermissions(!showAdvancedPermissions)}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <span>Permessi Avanzati (RBAC)</span>
              <svg
                className={`w-5 h-5 transition-transform ${showAdvancedPermissions ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showAdvancedPermissions && (
              <div className="mt-4 space-y-4">
                {/* Role Permissions Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-xs font-medium text-blue-900 mb-2">
                    Permessi del ruolo "{ROLE_LABELS[formData.role]}"
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {rolePermissions.map((perm: string) => (
                      <span
                        key={perm}
                        className="inline-block px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded"
                      >
                        {allPermissions[perm]?.label || perm}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Custom Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permessi Aggiuntivi
                    <span className="ml-1 text-xs text-gray-500">(aggiungi permessi oltre a quelli del ruolo)</span>
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {Object.entries(allPermissions).map(([key, info]: [string, any]) => {
                      const hasInRole = rolePermissions.includes(key);
                      const isCustom = formData.custom_permissions.includes(key);
                      const isDenied = formData.denied_permissions.includes(key);

                      return (
                        <label
                          key={key}
                          className={`flex items-center p-2 rounded hover:bg-gray-50 ${
                            hasInRole ? 'opacity-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isCustom}
                            disabled={hasInRole || isDenied}
                            onChange={() => togglePermission(key, 'custom')}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {info.label}
                            {hasInRole && <span className="ml-1 text-xs text-blue-600">(già nel ruolo)</span>}
                            {isDenied && <span className="ml-1 text-xs text-red-600">(negato)</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Denied Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permessi Negati
                    <span className="ml-1 text-xs text-gray-500">(rimuovi permessi anche se nel ruolo)</span>
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {Object.entries(allPermissions).map(([key, info]: [string, any]) => {
                      const isDenied = formData.denied_permissions.includes(key);
                      const isCustom = formData.custom_permissions.includes(key);

                      return (
                        <label
                          key={key}
                          className="flex items-center p-2 rounded hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={isDenied}
                            disabled={isCustom}
                            onChange={() => togglePermission(key, 'denied')}
                            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {info.label}
                            {isCustom && <span className="ml-1 text-xs text-green-600">(custom)</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Salvataggio...' : user ? 'Salva' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Password Reset Modal
interface PasswordResetModalProps {
  user: User;
  onSubmit: (password: string, temporary: boolean) => void;
  onClose: () => void;
  isLoading: boolean;
}

const PasswordResetModal = ({ user, onSubmit, onClose, isLoading }: PasswordResetModalProps) => {
  const [password, setPassword] = useState('');
  const [temporary, setTemporary] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isPasswordValid(password)) onSubmit(password, temporary);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Stai per reimpostare la password per <strong>{user.nome} {user.cognome}</strong> ({user.email})
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nuova Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <PasswordRequirements password={password} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={temporary} onChange={event => setTemporary(event.target.checked)} />
            Password temporanea: richiedi il cambio al prossimo accesso
          </label>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isLoading || !isPasswordValid(password)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Salvataggio...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Users = () => {
  const { user: currentUser, tenant } = useAuth();
  const { hasPermission, isHost, canViewUsers } = usePermissions();
  const queryClient = useQueryClient();
  const impersonateMutation = useMutation({
    mutationFn: (target: User) => tenantsAPI.impersonate(tenant!.id, target.id),
    onSuccess: async response => {
      await clearWebOfflineCaches();
      const data = response.data as unknown as { access_token: string; refresh_token: string };
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      window.location.href = '/';
    },
    onError: error => toast.error(passwordError(error)),
  });

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('');
  const [showHostUsers, setShowHostUsers] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  const [deactivateConfirmUser, setDeactivateConfirmUser] = useState<User | null>(null);

  // Cono di visibilità (issue #54): conteggio subordinati diretti per badge in tabella
  const { data: hierarchyForBadges, isLoading: hierarchyLoading, error: hierarchyError } = useQuery({
    queryKey: ['users-hierarchy'],
    queryFn: async () => {
      const response = await usersAPI.getHierarchy();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !showHostUsers && (canViewUsers || isHost),
  });
  const directReportsCount = new Map<string, number>();
  for (const u of hierarchyForBadges || []) {
    if (u.responsabile_id) {
      directReportsCount.set(u.responsabile_id, (directReportsCount.get(u.responsabile_id) || 0) + 1);
    }
  }

  // Fetch users - use host endpoint when viewing host users
  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, search, roleFilter, activeFilter, showHostUsers],
    enabled: canViewUsers || isHost,
    queryFn: async () => {
      if (showHostUsers && isHost) {
        const response = await usersAPI.getHostUsers({
          page,
          limit: 20,
          search: search || undefined,
          is_active: activeFilter === '' ? undefined : activeFilter,
        });
        return response.data;
      } else {
        const response = await usersAPI.getUsers({
          page,
          limit: 20,
          search: search || undefined,
          role: roleFilter || undefined,
          is_active: activeFilter === '' ? undefined : activeFilter,
        });
        return response.data;
      }
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: UserCreateRequest) =>
      showHostUsers && isHost ? usersAPI.createHostUser(data) : usersAPI.createUser(data),
    onSuccess: () => {
      toast.success('Utente creato con successo');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-hierarchy'] });
      setShowCreateModal(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione dell\'utente');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateRequest }) => usersAPI.updateUser(id, data),
    onSuccess: () => {
      toast.success('Utente aggiornato con successo');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-hierarchy'] });
      setEditingUser(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento dell\'utente');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersAPI.deleteUser(id),
    onSuccess: () => {
      toast.success('Utente eliminato con successo');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-hierarchy'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione dell\'utente');
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: (id: string) => usersAPI.activateUser(id),
    onSuccess: () => {
      toast.success('Utente attivato');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-hierarchy'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'attivazione');
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => usersAPI.deactivateUser(id),
    onSuccess: () => {
      toast.success('Utente disattivato');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-hierarchy'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante la disattivazione');
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password, temporary }: { id: string; password: string; temporary: boolean }) =>
      usersAPI.resetPassword(id, { new_password: password, temporary }),
    onSuccess: () => {
      toast.success('Password reimpostata con successo');
      setResetPasswordUser(null);
    },
    onError: (error: any) => {
      toast.error(passwordError(error));
    },
  });

  const handleDelete = (user: User) => {
    setDeleteConfirmUser(user);
  };

  const handleToggleActive = (user: User) => {
    if (user.is_active) {
      setDeactivateConfirmUser(user);
    } else {
      activateMutation.mutate(user.id);
    }
  };

  const handleFormSubmit = (formData: UserCreateRequest | UserUpdateRequest) => {
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: formData as UserUpdateRequest });
    } else {
      createMutation.mutate(formData as UserCreateRequest);
    }
  };

  const handleCloseForm = () => {
    setShowCreateModal(false);
    setEditingUser(null);
  };

  // Check permissions
  if (!canViewUsers && !isHost) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Accesso negato. Non hai i permessi per visualizzare gli utenti.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {showHostUsers ? 'Utenti Host' : 'Gestione Utenti'}
            </h1>
            <p className="text-gray-600 mt-1">
              {showHostUsers ? 'Gestisci gli amministratori della piattaforma' : 'Gestisci gli utenti del tenant'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isHost && (
              <button
                onClick={() => {
                  setShowHostUsers(!showHostUsers);
                  setShowHierarchy(false);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  showHostUsers
                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {showHostUsers ? 'Utenti Host' : 'Mostra Host'}
              </button>
            )}
            {(hasPermission('users:invite') || isHost) && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nuovo Utente
              </button>
            )}
          </div>
        </div>

        {!showHostUsers && (
          <div role="tablist" aria-label="Gestione utenti" className="flex gap-6 border-b border-gray-200 mb-6">
            {['Utenti', 'Gerarchie'].map((label, index) => {
              const selected = showHierarchy === (index === 1);
              return <button key={label} type="button" role="tab" id={`users-tab-${index}`} aria-selected={selected} aria-controls={`users-panel-${index}`} tabIndex={selected ? 0 : -1}
                onClick={() => setShowHierarchy(index === 1)}
                onKeyDown={event => {
                  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                  event.preventDefault();
                  const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
                  setShowHierarchy(next === 1);
                  document.getElementById(`users-tab-${next}`)?.focus();
                }}
                className={`px-1 py-3 text-sm font-medium border-b-2 -mb-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${selected ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                {label}
              </button>;
            })}
          </div>
        )}
        {showHierarchy && !showHostUsers && (
          <div role="tabpanel" id="users-panel-1" aria-labelledby="users-tab-1">
            {hierarchyLoading ? <p role="status">Caricamento gerarchia...</p> : hierarchyError ? <p role="alert" className="mb-4 text-red-700">Impossibile caricare la gerarchia utenti.</p> : <UserHierarchy users={hierarchyForBadges || []} />}
          </div>
        )}
        {/* Filters */}
        {(!showHierarchy || showHostUsers) && <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Cerca per nome, cognome o email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {!showHostUsers && (
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as UserRole | '');
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tutti i ruoli</option>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          )}
          <select
            value={activeFilter === '' ? '' : activeFilter ? 'true' : 'false'}
            onChange={(e) => {
              setActiveFilter(e.target.value === '' ? '' : e.target.value === 'true');
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tutti gli stati</option>
            <option value="true">Attivi</option>
            <option value="false">Disattivati</option>
          </select>
        </div>}
      </div>

      {/* Users List */}
      <div role={showHostUsers ? undefined : 'tabpanel'} id="users-panel-0" aria-labelledby={showHostUsers ? undefined : 'users-tab-0'} hidden={showHierarchy && !showHostUsers}>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Errore nel caricamento degli utenti</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ruolo
                </th>
                {showHostUsers && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ultimo Accesso
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.length === 0 ? (
                <tr>
                  <td colSpan={showHostUsers ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                    Nessun utente trovato
                  </td>
                </tr>
              ) : (
                data?.data.map((user: User) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          {user.avatar ? (
                            <img src={user.avatar} alt="" className="h-10 w-10 rounded-full" />
                          ) : (
                            <span className="text-sm font-medium text-gray-600">
                              {user.nome?.charAt(0)}{user.cognome?.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.nome} {user.cognome}
                            {user.id === currentUser?.id && (
                              <span className="ml-2 text-xs text-blue-600">(tu)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${ROLE_COLORS[user.role]}`}>
                        {ROLE_LABELS[user.role]}
                      </span>
                      {(directReportsCount.get(user.id) || 0) > 0 && (
                        <span
                          className="ml-1 px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800"
                          title="Subordinati diretti nel cono di visibilità"
                        >
                          {directReportsCount.get(user.id)} subordinat{directReportsCount.get(user.id) === 1 ? 'o' : 'i'}
                        </span>
                      )}
                    </td>
                    {showHostUsers && (
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Host
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Attivo' : 'Disattivato'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.ultimo_accesso
                        ? new Date(user.ultimo_accesso).toLocaleString('it-IT')
                        : 'Mai'}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {(hasPermission('users:edit') || isHost) && user.id !== currentUser?.id && (
                          <>
                            <button
                              onClick={() => handleToggleActive(user)}
                              className={user.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                              title={user.is_active ? 'Disattiva' : 'Attiva'}
                            >
                              {user.is_active ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => setResetPasswordUser(user)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Reset Password"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </button>
                          </>
                        )}
                        {tenant && currentUser?.role === 'admin' && hasPermission('users:edit') && !currentUser.impersonating && user.id !== currentUser.id && user.is_active && user.tenant_id === tenant.id && (
                          <button type="button" onClick={() => impersonateMutation.mutate(user)} disabled={impersonateMutation.isPending} className="text-purple-700 hover:text-purple-900 disabled:opacity-50" title="Impersona utente" aria-label="Impersona utente">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19.5a6 6 0 00-12 0M12 7.5a3 3 0 11-6 0 3 3 0 016 0zM15 12h6m-3-3l3 3-3 3" />
                            </svg>
                          </button>
                        )}
                        {(hasPermission('users:edit') || isHost) && (
                          <button
                            onClick={() => setEditingUser(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Modifica"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        {(hasPermission('users:delete') || isHost) && user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDelete(user)}
                            className="text-red-600 hover:text-red-900"
                            title="Elimina"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {data?.pagination && data.pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                  disabled={page === data.pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Successivo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Pagina <span className="font-medium">{page}</span> di{' '}
                    <span className="font-medium">{data.pagination.pages}</span>
                    {' '}({data.pagination.total} utenti totali)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                      disabled={page === data.pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingUser) && (
        <UserForm
          user={editingUser}
          onSubmit={handleFormSubmit}
          onClose={handleCloseForm}
          isLoading={createMutation.isPending || updateMutation.isPending}
          isHostMode={showHostUsers}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <PasswordResetModal
          user={resetPasswordUser}
          onSubmit={(password, temporary) => resetPasswordMutation.mutate({ id: resetPasswordUser.id, password, temporary })}
          onClose={() => setResetPasswordUser(null)}
          isLoading={resetPasswordMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirmUser}
        onClose={() => setDeleteConfirmUser(null)}
        onConfirm={() => {
          if (deleteConfirmUser) {
            deleteMutation.mutate(deleteConfirmUser.id);
            setDeleteConfirmUser(null);
          }
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare l'utente "${deleteConfirmUser?.nome} ${deleteConfirmUser?.cognome}"?`}
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Deactivate Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deactivateConfirmUser}
        onClose={() => setDeactivateConfirmUser(null)}
        onConfirm={() => {
          if (deactivateConfirmUser) {
            deactivateMutation.mutate(deactivateConfirmUser.id);
            setDeactivateConfirmUser(null);
          }
        }}
        title="Conferma Disattivazione"
        message={`Disattivare l'utente "${deactivateConfirmUser?.nome} ${deactivateConfirmUser?.cognome}"?`}
        confirmLabel="Disattiva"
        variant="warning"
        isLoading={deactivateMutation.isPending}
      />
    </div>
  );
};

export default Users;
